import { useQuery } from '@tanstack/react-query';
import { features } from '@ui/components/Features';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { getBoostMultiplerForPool } from '@ui/queries/sdk';
import type { GardenType } from '@ui/utils/garden';
import { currentWallet } from '@veno-app/wallet';

import { useCoinMode } from './useCoinMode';
import { isFeatureEnabled } from './useIsFeatureEnabled';

const { useAccount } = currentWallet;

const tectonicEnabled = isFeatureEnabled(features.TectonicGarden);

const useBoostMultiplier = (type: GardenType) => {
  const coinMode = useCoinMode();
  const sdk = useVenoSDK();
  const account = useAccount();
  const ferroBoostMultiplierQuery = getBoostMultiplerForPool(
    coinMode,
    sdk,
    account ?? '',
    'FERRO',
  );
  const vvsBoostMultiplierQuery = getBoostMultiplerForPool(
    coinMode,
    sdk,
    account ?? '',
    'VVS',
  );
  const vvsCroBoostMultiplierQuery = getBoostMultiplerForPool(
    coinMode,
    sdk,
    account ?? '',
    'VVS_CRO',
  );
  const tectonicBoostMultiplierQuery = getBoostMultiplerForPool(
    coinMode,
    sdk,
    account ?? '',
    'TECTONIC',
  );

  const { data: ferroBoostMultiplier, isLoading: ferroBoostMultiplierLoading } =
    useQuery(
      ferroBoostMultiplierQuery.queryKey,
      ferroBoostMultiplierQuery.queryFn,
      {
        enabled: type === 'FERRO' && !!account,
      },
    );
  const { data: vvsBoostMultiplier, isLoading: vvsBoostMultiplierLoading } =
    useQuery(
      vvsBoostMultiplierQuery.queryKey,
      vvsBoostMultiplierQuery.queryFn,
      {
        enabled: type === 'VVS' && !!account,
      },
    );
  const {
    data: vvsCroBoostMultiplier,
    isLoading: vvsCroBoostMultiplierLoading,
  } = useQuery(
    vvsCroBoostMultiplierQuery.queryKey,
    vvsCroBoostMultiplierQuery.queryFn,
    {
      enabled: type === 'VVS_CRO' && !!account,
    },
  );
  const {
    data: tectonicBoostMultiplier,
    isLoading: tectonicBoostMultiplierLoading,
  } = useQuery(
    tectonicBoostMultiplierQuery.queryKey,
    tectonicBoostMultiplierQuery.queryFn,
    {
      enabled: tectonicEnabled && !!account && type === 'TECTONIC',
    },
  );

  switch (type) {
    case 'FERRO':
      return {
        isLoading: ferroBoostMultiplierLoading,
        data: ferroBoostMultiplier,
      };
    case 'TECTONIC':
      return {
        isLoading: tectonicBoostMultiplierLoading,
        data: tectonicBoostMultiplier,
      };
    case 'VVS':
      return {
        isLoading: vvsBoostMultiplierLoading,
        data: vvsBoostMultiplier,
      };
    case 'VVS_CRO':
      return {
        isLoading: vvsCroBoostMultiplierLoading,
        data: vvsCroBoostMultiplier,
      };
  }
};

export default useBoostMultiplier;
